import React, { useEffect, useState } from 'react';
import { Btn, Modal, Input, Select } from '../shared/UI';
import { fmtDate } from '../../utils/helpers';
import { useT } from '../../i18n';
import api from '../../utils/api';

export const FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'yearly'];
const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Creates a schedule from an invoice (doc), or edits an existing schedule's timing (schedule).
export default function RecurringModal({ open, onClose, doc, schedule, onSaved, show }) {
  const { t } = useT();
  const [form, setForm] = useState({ frequency: 'monthly', interval: 1, startDate: '', endDate: '', dueDays: 30, autoSend: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      setForm({
        frequency: schedule.frequency, interval: schedule.interval,
        startDate: String(schedule.startDate).slice(0, 10), endDate: schedule.endDate ? String(schedule.endDate).slice(0, 10) : '',
        dueDays: schedule.dueDays, autoSend: schedule.autoSend,
      });
    } else {
      // Default: same day next month, with the payment terms of the source invoice
      const next = new Date(); next.setMonth(next.getMonth() + 1);
      const terms = doc?.dueDate && doc?.issueDate
        ? Math.max(0, Math.round((new Date(doc.dueDate) - new Date(doc.issueDate)) / 86400000)) : 30;
      setForm({ frequency: 'monthly', interval: 1, startDate: iso(next), endDate: '', dueDays: terms, autoSend: false });
    }
  }, [open]); // only when opening — must not clobber what the user is typing

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const clientEmail = schedule ? schedule.client?.email : doc?.clientSnapshot?.email;

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = schedule
        ? await api.put(`/recurring/${schedule._id}`, form)
        : await api.post('/recurring', { ...form, sourceDocId: doc._id });
      show(schedule ? t('recurring.saved') : t('recurring.created', { date: data.nextRunAt ? fmtDate(data.nextRunAt) : '—' }));
      onSaved?.(data);
      onClose();
    } catch (err) { show(err.response?.data?.message || t('recurring.failed'), 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('recurring.modalTitle', { n: schedule ? (schedule.label || '') : doc?.docNumber })} width={520}>
      <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!schedule && <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{t('recurring.modalIntro')}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 12, alignItems: 'end' }}>
          <Input label={t('recurring.every')} type="number" min="1" max="24" required value={form.interval} onChange={set('interval')} />
          <Select label={t('recurring.frequency')} value={form.frequency} onChange={set('frequency')}>
            {FREQUENCIES.map(f => <option key={f} value={f}>{t(`recurring.f_${f}`)}</option>)}
          </Select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12 }}>
          <Input label={t('recurring.firstInvoice')} type="date" required value={form.startDate} onChange={set('startDate')} />
          <Input label={t('recurring.endDate')} type="date" min={form.startDate} value={form.endDate} onChange={set('endDate')} />
        </div>
        <Input label={t('recurring.dueDays')} type="number" min="0" max="365" required value={form.dueDays} onChange={set('dueDays')} style={{ maxWidth: 240 }} />
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text)', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.autoSend} onChange={set('autoSend')} /> {t('recurring.autoSend')}
          </label>
          <p style={{ fontSize: 12, color: form.autoSend && !clientEmail ? 'var(--danger)' : 'var(--text-subtle)', marginTop: 4 }}>
            {form.autoSend && !clientEmail ? t('recurring.noClientEmail') : t('recurring.autoSendHelp')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn variant="primary" type="submit" disabled={saving}>{saving ? t('common.saving') : schedule ? t('editor.saveChanges') : `🔁 ${t('recurring.create')}`}</Btn>
        </div>
      </form>
    </Modal>
  );
}
